import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Settings as SettingsIcon,
    LogOut,
    Sparkles,
    Pencil,
    BadgeCheck,
    Bookmark,
    CalendarDays,
    Briefcase,
    ShieldCheck,
    ChevronRight,
    Activity as ActivityIcon,
    Wallet as WalletIcon,
    Heart,
    Clock,
    Share2,
    MapPin,
    X as XIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import InitialAvatar from "@/components/InitialAvatar";
import {
    listEventsByOwner,
    listServicesByOwner,
    getAllEvents,
    getAllServices,
    isEventPast,
} from "@/lib/entitiesStore";
import { getSaved, getInterestedEvents, getJoinedEvents, getFollowing } from "@/lib/socialStore";
import { buildActivityFeed } from "@/lib/analyticsStore";
import { getBookingsBySeller } from "@/lib/bookingStore";
import { useSocialVersion, timeAgo } from "@/lib/hooks";
import { EventCard } from "@/components/EventCard";
import { ServiceCard } from "@/components/ServiceCard";

const TABS = [
    { id: "activity", label: "Activity", icon: ActivityIcon },
    { id: "services", label: "Services", icon: Briefcase },
    { id: "events",   label: "Events",   icon: CalendarDays },
    { id: "saved",    label: "Saved",    icon: Bookmark },
];

const SHORTCUTS = [
    { id: "services", label: "My Services", icon: Briefcase,    to: "/manage" },
    { id: "events",   label: "My Events",   icon: CalendarDays, to: "/manage" },
    { id: "wallet",   label: "Wallet",      icon: WalletIcon,   to: "/wallet" },
    { id: "settings", label: "Settings",    icon: SettingsIcon, to: "/settings" },
];

const COMPLETION_KEY = (uid) => `neara.profileCompletion.dismissed.${uid}`;

const Profile = () => {
    const navigate = useNavigate();
    const { user, signOut } = useAuth();
    const v = useSocialVersion();
    const [tab, setTab] = useState("activity");

    const userServices = useMemo(() => (user ? listServicesByOwner(user.id) : []), [user, v]);
    const userEvents = useMemo(() => (user ? listEventsByOwner(user.id) : []), [user, v]);
    const saved = useMemo(() => (user ? getSaved(user.id) : { services: [], events: [] }), [user, v]);
    const savedServices = useMemo(() => {
        const all = getAllServices();
        return saved.services.map((id) => all.find((s) => s.id === id)).filter(Boolean);
    }, [saved]);
    const savedEvents = useMemo(() => {
        const all = getAllEvents();
        return saved.events.map((id) => all.find((e) => e.id === id)).filter(Boolean);
    }, [saved]);
    const interestedEvents = useMemo(() => {
        if (!user) return [];
        const ids = getInterestedEvents(user.id);
        const all = getAllEvents();
        return ids.map((id) => all.find((e) => e.id === id)).filter(Boolean);
    }, [user, v]);

    /* ---- Stats (Activity / Followers / Following) ---- */
    const activityCount =
        userServices.length +
        userEvents.length +
        saved.services.length +
        saved.events.length +
        interestedEvents.length;
    const followersCount = user?.followersCount ?? 0;
    const followingCount = (user ? getFollowing(user.id).length : 0) || (user?.followingCount ?? 0);

    // Completed jobs = released bookings where this user was the seller.
    const completedJobsCount = useMemo(
        () => (user ? (getBookingsBySeller(user.id) || []).filter((b) => b.status === "released").length : 0),
        [user, v]
    );

    // Activity feed — computed from bookings + joined events + listings.
    const activityFeed = useMemo(
        () => (user ? buildActivityFeed(user.id) : []),
        [user, v]
    );

    const isPremium = !!user?.premium;
    const isVerified = !!user?.verified;
    // Phase 3D verification states: "none" | "pending" | "verified" | "rejected"
    const verificationStatus = (() => {
        if (user?.verified) return "verified";
        if (user?.verificationStatus) return user.verificationStatus;
        if (user?.verification?.status === "pending") return "pending";
        return "none";
    })();
    const pendingVerification = verificationStatus === "pending";
    const rejectedVerification = verificationStatus === "rejected";

    const completion = [
        { id: "avatar",   label: "Profile photo",             done: !!user?.avatar },
        { id: "bio",      label: "Bio",                       done: !!(user?.bio && user.bio.trim()) },
        { id: "location", label: "Location",                  done: !!(user?.location && String(user.location).trim()) },
        { id: "contact",  label: "Phone / email verified",    done: !!(user?.phone || user?.email) },
    ];
    const completedCount = completion.filter((c) => c.done).length;
    const completionPct = Math.round((completedCount / completion.length) * 100);

    const completionDismissed = !!localStorage.getItem(COMPLETION_KEY(user?.id || "anon"));
    const [hideCompletion, setHideCompletion] = useState(completionDismissed);
    const dismissCompletion = () => {
        localStorage.setItem(COMPLETION_KEY(user?.id || "anon"), "1");
        setHideCompletion(true);
    };

    const handleLogout = () => {
        signOut();
        navigate("/auth", { replace: true });
    };

    return (
        <div className="min-h-full animate-fade-in pb-8" data-testid="profile-screen">
            <header
                className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4"
                style={{ background: "linear-gradient(180deg, hsl(var(--primary) / 0.08) 0%, transparent 100%)" }}
            >
                <div className="flex items-start justify-between">
                    <h1 className="font-display text-[20px] font-semibold tracking-tight pt-1">Profile</h1>
                    <div className="flex items-center gap-1.5">
                        <button
                            data-testid="profile-settings-btn"
                            onClick={() => navigate("/settings")}
                            className="h-10 w-10 flex items-center justify-center rounded-xl bg-secondary hover:bg-muted active:scale-95"
                            aria-label="Settings"
                        >
                            <SettingsIcon size={18} />
                        </button>
                        <button
                            data-testid="profile-logout-btn"
                            onClick={handleLogout}
                            className="h-10 w-10 flex items-center justify-center rounded-xl bg-secondary hover:bg-muted active:scale-95 text-destructive"
                            aria-label="Log out"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                </div>

                <div className="mt-4 flex items-center gap-4">
                    <div className="relative">
                        {user?.avatar ? (
                            <img
                                src={user.avatar}
                                alt={user.fullName}
                                className={[
                                    "h-20 w-20 rounded-full object-cover",
                                    isPremium
                                        ? "ring-2 ring-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]"
                                        : "ring-2 ring-border",
                                ].join(" ")}
                                data-testid="profile-avatar"
                            />
                        ) : (
                            <div data-testid="profile-avatar">
                                <InitialAvatar name={user?.fullName || "?"} size={80} />
                            </div>
                        )}
                        {isPremium && (
                            <span
                                className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full flex items-center justify-center shadow-card"
                                style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.7))" }}
                                aria-label="Premium"
                                data-testid="profile-premium-badge"
                            >
                                <Sparkles size={13} className="text-primary-foreground" />
                            </span>
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="font-display text-[18px] font-semibold tracking-tight truncate flex items-center gap-1.5">
                            {user?.fullName || "—"}
                            {isVerified && <BadgeCheck size={16} className="text-primary shrink-0" data-testid="profile-verified-badge" />}
                        </h2>
                        <p className="text-[13px] text-muted-foreground truncate">@{user?.username || ""}</p>
                        {user?.location && (
                            <p
                                data-testid="profile-location"
                                className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-muted-foreground"
                            >
                                <MapPin size={11} />
                                {user.location}
                            </p>
                        )}
                        {(userServices.length > 0 || userEvents.length > 0) && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="profile-role-tags">
                                {userServices.length > 0 && (
                                    <span
                                        data-testid="profile-tag-provider"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/25 text-[10.5px] font-semibold"
                                    >
                                        <Briefcase size={10} />
                                        Service Provider
                                    </span>
                                )}
                                {userEvents.length > 0 && (
                                    <span
                                        data-testid="profile-tag-host"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/25 text-[10.5px] font-semibold"
                                    >
                                        <CalendarDays size={10} />
                                        Event Host
                                    </span>
                                )}
                                {completedJobsCount > 0 && (
                                    <span
                                        data-testid="profile-tag-completed-jobs"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/25 text-[10.5px] font-semibold"
                                    >
                                        <BadgeCheck size={10} />
                                        {completedJobsCount} completed job{completedJobsCount === 1 ? "" : "s"}
                                    </span>
                                )}
                            </div>
                        )}
                        {user?.bio && (
                            <p className="mt-1 text-[12.5px] text-foreground/80 line-clamp-2" data-testid="profile-bio">
                                {user.bio}
                            </p>
                        )}
                    </div>
                </div>

                {/* Stats: Activity / Followers / Following */}
                <div className="mt-4 grid grid-cols-3 gap-2 rounded-2xl border border-border bg-card shadow-soft p-2">
                    <Stat label="Activity"  value={activityCount}  testId="stat-activity" />
                    <Stat label="Followers" value={followersCount} testId="stat-followers" onClick={() => navigate("/profile/followers")} />
                    <Stat label="Following" value={followingCount} testId="stat-following" onClick={() => navigate("/profile/following")} />
                </div>

                <div className="mt-3 flex gap-2">
                    <button
                        data-testid="profile-edit-btn"
                        onClick={() => navigate("/profile/edit")}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-secondary text-secondary-foreground hover:bg-muted active:scale-[0.98] font-medium text-[13.5px]"
                    >
                        <Pencil size={14} />
                        Edit profile
                    </button>
                    <button
                        data-testid="profile-share-btn"
                        onClick={async () => {
                            const url =
                                typeof window !== "undefined"
                                    ? `${window.location.origin}/u/${user?.username || ""}`
                                    : "";
                            try {
                                if (navigator.share) {
                                    await navigator.share({
                                        title: user?.fullName || "Neyra profile",
                                        text: `Check out @${user?.username || ""} on Neyra`,
                                        url,
                                    });
                                } else if (navigator.clipboard && url) {
                                    await navigator.clipboard.writeText(url);
                                }
                            } catch { /* ignore — user dismissed share */ }
                        }}
                        className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full bg-secondary text-secondary-foreground hover:bg-muted active:scale-[0.98] font-medium text-[13.5px]"
                        aria-label="Share profile"
                        title="Share profile"
                    >
                        <Share2 size={14} />
                        Share Profile
                    </button>
                    {!isPremium && (
                        <button
                            data-testid="profile-premium-cta"
                            onClick={() => navigate("/premium")}
                            className="inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full text-primary-foreground font-medium text-[13.5px] active:scale-[0.98]"
                            style={{
                                background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                            }}
                        >
                            <Sparkles size={14} />
                            Go Premium
                        </button>
                    )}
                </div>
            </header>

            {/* Shortcuts — directly below Edit Profile */}
            <section className="px-4 pt-4" data-testid="profile-shortcuts">
                <div className="grid grid-cols-4 gap-2">
                    {SHORTCUTS.map((s) => (
                        <Shortcut key={s.id} {...s} onClick={() => navigate(s.to)} />
                    ))}
                </div>
            </section>

            {/* Profile completion + verification banner */}
            <div className="px-4 pt-4 space-y-3">
                {/* Upgrade to Business — personal accounts only */}
                {user?.accountType !== "business" && (
                    <button
                        type="button"
                        data-testid="profile-upgrade-business-btn"
                        onClick={() => navigate("/profile/upgrade-business")}
                        className="w-full p-4 rounded-2xl border border-primary/30 bg-primary/5 hover:bg-primary/10 active:scale-[0.99] transition-[transform,background-color] text-left flex items-center gap-3"
                    >
                        <span className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                            <Briefcase size={18} />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block font-display text-[13.5px] font-semibold">Upgrade to Business</span>
                            <span className="block text-[11.5px] text-muted-foreground mt-0.5 truncate">
                                Sell services, host events, accept bookings
                            </span>
                        </span>
                        <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                    </button>
                )}

                {completionPct < 100 && !hideCompletion && (
                    <div
                        data-testid="profile-completion-card"
                        className="relative w-full p-4 rounded-2xl border border-border bg-card shadow-soft"
                    >
                        <button
                            type="button"
                            data-testid="profile-completion-dismiss"
                            onClick={dismissCompletion}
                            aria-label="Dismiss"
                            className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
                        >
                            <XIcon size={14} />
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/profile/edit")}
                            className="w-full text-left active:opacity-90"
                        >
                            <div className="flex items-center justify-between pr-8">
                                <span className="font-display text-[13.5px] font-semibold">Complete your profile</span>
                                <span className="text-[12px] font-semibold text-primary">{completionPct}%</span>
                            </div>
                            <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                                <div
                                    className="h-full rounded-full transition-[width] duration-500"
                                    data-testid="profile-completion-bar"
                                    style={{
                                        width: `${completionPct}%`,
                                        background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.65))",
                                    }}
                                />
                            </div>
                            <ul className="mt-3 grid grid-cols-1 gap-1.5 text-[12px]">
                                {completion.map((c) => (
                                    <li key={c.id} className="flex items-center gap-2" data-testid={`profile-completion-${c.id}`}>
                                        <span
                                            className={[
                                                "h-4 w-4 rounded-full flex items-center justify-center text-[9px] font-bold",
                                                c.done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                                            ].join(" ")}
                                        >
                                            {c.done ? "✓" : ""}
                                        </span>
                                        <span className={c.done ? "text-muted-foreground line-through" : ""}>{c.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </button>
                    </div>
                )}

                {!isVerified && (
                    <button
                        data-testid="profile-verification-banner"
                        onClick={() => navigate(user?.accountType === "business" ? "/verification/business" : "/verification/personal")}
                        className={[
                            "w-full flex items-center gap-3 p-3.5 rounded-2xl border text-left active:scale-[0.99] transition-transform",
                            rejectedVerification
                                ? "border-destructive/40 bg-destructive/5"
                                : "border-primary/30 bg-primary/5",
                        ].join(" ")}
                    >
                        <span className={[
                            "h-10 w-10 rounded-xl flex items-center justify-center",
                            rejectedVerification ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary",
                        ].join(" ")}>
                            <ShieldCheck size={18} />
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className="block font-display text-[13.5px] font-semibold">
                                {rejectedVerification
                                    ? "Verification Rejected"
                                    : pendingVerification
                                        ? "Verification Pending"
                                        : "Get Verified"}
                            </span>
                            <span className="block text-[11.5px] text-muted-foreground truncate">
                                {rejectedVerification
                                    ? (user?.rejectionReason
                                        ? `${user.rejectionReason} · Tap to resubmit`
                                        : "Tap to resubmit your documents.")
                                    : pendingVerification
                                        ? "Your verification has been submitted. Our admin team is reviewing your documents."
                                        : "Build trust and get more bookings."}
                            </span>
                        </span>
                        <ChevronRight size={16} className="text-muted-foreground" />
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="px-4 pt-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="profile-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            data-testid={`profile-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={[
                                "shrink-0 h-10 px-3.5 rounded-xl text-[12.5px] font-medium inline-flex items-center justify-center gap-1.5 transition-colors",
                                tab === t.id
                                    ? "bg-primary text-primary-foreground shadow-soft"
                                    : "bg-secondary text-secondary-foreground hover:bg-muted",
                            ].join(" ")}
                        >
                            <Icon size={14} strokeWidth={2.2} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="px-4 pt-4" data-testid={`profile-tab-content-${tab}`}>
                {tab === "activity" && (
                    <ActivityTab
                        userServices={userServices}
                        userEvents={userEvents}
                        savedServices={savedServices}
                        savedEvents={savedEvents}
                        interestedEvents={interestedEvents}
                    />
                )}
                {tab === "services" && (
                    <ProfileServicesGrid
                        items={userServices}
                        emptyLabel="You haven't created any services yet"
                        ctaLabel="Create Service"
                        ctaTo="/create/service"
                    />
                )}
                {tab === "events" && (
                    <ProfileEventsTab
                        userId={user?.id}
                        hostingEvents={userEvents}
                        v={v}
                    />
                )}
                {tab === "saved" && <SavedTab services={savedServices} events={savedEvents} />}
            </div>
        </div>
    );
};

const Shortcut = ({ label, icon: Icon, onClick, id }) => (
    <button
        data-testid={`profile-shortcut-${id}`}
        onClick={onClick}
        className="flex flex-col items-center gap-1.5 p-2 rounded-2xl border border-border bg-card shadow-soft active:scale-[0.97] hover:bg-accent/40 transition-[transform,background-color] duration-150"
    >
        <span
            className="h-10 w-10 rounded-xl flex items-center justify-center text-primary"
            style={{
                background:
                    "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.06) 60%, transparent 100%)",
            }}
        >
            <Icon size={18} strokeWidth={2.2} />
        </span>
        <span className="text-[11.5px] font-medium text-foreground/85">{label}</span>
    </button>
);

const Stat = ({ label, value, testId, onClick }) => {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            data-testid={testId}
            className={[
                "flex flex-col items-center py-1.5",
                onClick ? "rounded-xl hover:bg-accent/40 active:scale-[0.97] transition-[transform,background-color] cursor-pointer" : "",
            ].join(" ")}
        >
            <span className="font-display text-[17px] font-semibold tracking-tight">{value}</span>
            <span className="text-[11px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</span>
        </Tag>
    );
};

const ActivityTab = ({ userServices, userEvents, savedServices, savedEvents, interestedEvents }) => {
    // Build a timeline: newest first. Use createdAt for created items,
    // or fallback to current time for saved/interested (no stored timestamp).
    const items = [
        ...userServices.map((s) => ({
            id: "svc-" + s.id,
            kind: "created-service",
            icon: Briefcase,
            title: "You created a service",
            detail: s.name,
            ts: s.createdAt ? new Date(s.createdAt).getTime() : Date.now(),
            to: `/services/${s.id}`,
        })),
        ...userEvents.map((e) => ({
            id: "evt-" + e.id,
            kind: "created-event",
            icon: CalendarDays,
            title: "You created an event",
            detail: e.title,
            ts: e.createdAt ? new Date(e.createdAt).getTime() : Date.now(),
            to: `/events/${e.id}`,
        })),
        ...savedServices.map((s, i) => ({
            id: "saved-svc-" + s.id,
            kind: "saved",
            icon: Bookmark,
            title: "Saved a service",
            detail: s.name,
            ts: Date.now() - (i + 1) * 3_600_000,
            to: `/services/${s.id}`,
        })),
        ...savedEvents.map((e, i) => ({
            id: "saved-evt-" + e.id,
            kind: "saved",
            icon: Bookmark,
            title: "Saved an event",
            detail: e.title,
            ts: Date.now() - (i + 1) * 3_600_000,
            to: `/events/${e.id}`,
        })),
        ...interestedEvents.map((e, i) => ({
            id: "int-" + e.id,
            kind: "interested",
            icon: Heart,
            title: "Interested in an event",
            detail: e.title,
            ts: Date.now() - (i + 1) * 7_200_000,
            to: `/events/${e.id}`,
        })),
    ].sort((a, b) => b.ts - a.ts);

    const navigate = useNavigate();

    if (items.length === 0) {
        return <EmptyTab label="Your activity will appear here" />;
    }

    return (
        <ul className="space-y-2" data-testid="profile-activity-list">
            {items.map((it) => {
                const Icon = it.icon;
                return (
                    <li key={it.id}>
                        <button
                            data-testid={`profile-activity-${it.id}`}
                            onClick={() => navigate(it.to)}
                            className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card hover:bg-accent/40 active:scale-[0.99] transition-[transform,background-color] duration-150 text-left"
                        >
                            <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                <Icon size={16} strokeWidth={2.2} />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-semibold">{it.title}</div>
                                <div className="text-[11.5px] text-muted-foreground truncate">
                                    {it.detail}
                                </div>
                            </div>
                            <span className="text-[11px] text-muted-foreground shrink-0 inline-flex items-center gap-1">
                                <Clock size={10} />
                                {timeAgo(it.ts)}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

const ProfileServicesGrid = ({ items, emptyLabel, ctaLabel, ctaTo }) => {
    const navigate = useNavigate();
    if (items.length === 0) {
        return <EmptyTab label={emptyLabel} ctaLabel={ctaLabel} onCta={() => navigate(ctaTo)} />;
    }
    return (
        <div className="grid grid-cols-2 gap-3">
            {items.map((s) => <ServiceCard key={s.id} service={s} />)}
        </div>
    );
};

const ProfileEventsTab = ({ userId, hostingEvents, v }) => {
    const navigate = useNavigate();
    const [sub, setSub] = useState("hosting");

    const joinedEvents = useMemo(() => {
        if (!userId) return [];
        const ids = getJoinedEvents(userId);
        const all = getAllEvents();
        return ids
            .map((id) => all.find((e) => e.id === id))
            .filter((e) => e && e.ownerId !== userId);
    }, [userId, v]);

    const upcomingHosting = hostingEvents.filter((e) => !isEventPast(e));
    const pastHosting = hostingEvents.filter(isEventPast);
    const pastJoined = joinedEvents.filter(isEventPast);
    const upcomingJoined = joinedEvents.filter((e) => !isEventPast(e));
    const pastEvents = [...pastHosting, ...pastJoined];

    const SUBS = [
        { id: "hosting", label: `Hosting · ${upcomingHosting.length}` },
        { id: "joined",  label: `Joined · ${upcomingJoined.length}` },
        { id: "past",    label: `Past · ${pastEvents.length}` },
    ];

    const list =
        sub === "hosting" ? upcomingHosting :
        sub === "joined"  ? upcomingJoined  :
        pastEvents;

    return (
        <div data-testid="profile-events-tab">
            <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar" data-testid="profile-events-subtabs">
                {SUBS.map((s) => (
                    <button
                        key={s.id}
                        data-testid={`profile-events-subtab-${s.id}`}
                        onClick={() => setSub(s.id)}
                        className={[
                            "shrink-0 h-9 px-3.5 rounded-full text-[12px] font-medium transition-colors",
                            sub === s.id
                                ? "bg-primary text-primary-foreground"
                                : "bg-secondary text-secondary-foreground hover:bg-muted",
                        ].join(" ")}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
            {list.length === 0 ? (
                <EmptyTab
                    label={
                        sub === "hosting" ? "You haven't created any events yet" :
                        sub === "joined"  ? "You haven't joined any events yet"  :
                        "No past events yet"
                    }
                    ctaLabel={sub === "hosting" ? "Create Event" : null}
                    onCta={sub === "hosting" ? () => navigate("/create/event") : null}
                />
            ) : (
                <div className="grid grid-cols-1 gap-3" data-testid={`profile-events-list-${sub}`}>
                    {list.map((e) => <EventCard key={e.id} event={e} />)}
                </div>
            )}
        </div>
    );
};

const SavedTab = ({ services, events }) => {
    if (!services.length && !events.length) {
        return <EmptyTab label="Nothing saved yet" />;
    }
    return (
        <div className="space-y-5">
            {services.length > 0 && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2 ml-1">
                        Services
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                        {services.map((s) => <ServiceCard key={s.id} service={s} />)}
                    </div>
                </div>
            )}
            {events.length > 0 && (
                <div>
                    <h3 className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2 ml-1">
                        Events
                    </h3>
                    <div className="grid grid-cols-1 gap-3">
                        {events.map((e) => <EventCard key={e.id} event={e} />)}
                    </div>
                </div>
            )}
        </div>
    );
};

const EmptyTab = ({ label, ctaLabel, onCta }) => (
    <div className="py-12 text-center" data-testid="profile-tab-empty">
        <p className="text-[13.5px] text-muted-foreground">{label}</p>
        {ctaLabel && (
            <button
                data-testid="profile-tab-empty-cta"
                onClick={onCta}
                className="mt-4 inline-flex items-center justify-center h-11 px-5 rounded-full text-primary-foreground font-medium text-[13px] active:scale-[0.98]"
                style={{
                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                    boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                }}
            >
                {ctaLabel}
            </button>
        )}
    </div>
);

export default Profile;
